import React from 'react';
import {useTranslation} from 'react-i18next';

import {Button, Modal, ModalActions, ModalHeader, showModal} from '@edna/components';

import {SecondaryText} from 'src/components/primitives';
import {
  EPaymentType,
  useAcceptTariffsMutation,
  useGetBillingFlagQuery,
  useGetTenantDataQuery,
} from 'src/containers/User';

import {Banner} from '../Banner';
import {AvailableTariffsList} from './TariffsList';
import * as S from './style';

const NeedAcceptTariffsBanner = React.memo(() => {
  const {t} = useTranslation();
  const {data: tenantData} = useGetTenantDataQuery();

  const isPrePaymentType = tenantData?.paymentType === EPaymentType.PREPAYMENT;
  const [acceptTariffs] = useAcceptTariffsMutation();

  return (
    <Banner
      type="ERROR"
      title={t('Tariffs:acceptTariffs.banner', {
        buttonName: isPrePaymentType ? t('Common:button.accept') : t('Tariffs:acceptTariffs.fine'),
      })}
      noMargin
      shadowSize="XS"
    >
      <Button
        appearance="primary"
        onClick={() => acceptTariffs()}
        data-selector="Common:button.accept"
      >
        {isPrePaymentType ? t('Common:button.accept') : t('Tariffs:acceptTariffs.fine')}
      </Button>
    </Banner>
  );
});

NeedAcceptTariffsBanner.displayName = 'NeedAcceptTariffsBanner';

export const AcceptTariffsModal = React.memo<{
  closeModal: TEmptyFunction;
}>(({closeModal}) => {
  const {t} = useTranslation();
  const {data: tenantData} = useGetTenantDataQuery();

  const isPrePaymentType = tenantData?.paymentType === EPaymentType.PREPAYMENT;
  const [acceptTariffs] = useAcceptTariffsMutation();

  const handleSubmit = React.useCallback(async () => {
    acceptTariffs();
    closeModal();
  }, [closeModal]);

  return (
    <Modal width="704px">
      <ModalHeader>{t('Tariffs:acceptTariffsModal.title')}</ModalHeader>
      <SecondaryText bottomIndent="6">
        {t('Tariffs:acceptTariffsModal.description', {
          buttonName: isPrePaymentType
            ? t('Tariffs:acceptTariffs.acceptTariffs')
            : t('Tariffs:acceptTariffs.fine'),
        })}
      </SecondaryText>
      <S.ScrollContainer>
        <AvailableTariffsList />
      </S.ScrollContainer>
      <ModalActions>
        <Button appearance="primary" onClick={handleSubmit} data-selector="Tariffs:acceptTariffs">
          {isPrePaymentType
            ? t('Tariffs:acceptTariffs.acceptTariffs')
            : t('Tariffs:acceptTariffs.fine')}
        </Button>
      </ModalActions>
    </Modal>
  );
});

AcceptTariffsModal.displayName = 'AcceptTariffsModal';

const showAcceptTariffsModal = () =>
  showModal((closeModal) => <AcceptTariffsModal closeModal={closeModal} />);

const useAcceptTariffsFlag = (config: {skip?: boolean} | void) => {
  const {data: tenantData} = useGetTenantDataQuery(undefined, {skip: config?.skip});
  const {data: hasBilling} = useGetBillingFlagQuery(undefined, {skip: config?.skip});

  return hasBilling && tenantData && !tenantData.tariffsAcceptedAt && !tenantData.selfRegistered;
};

const useAcceptTariffsAction = (config: {skip?: boolean} | void) => {
  const isNeedAcceptTariffs = useAcceptTariffsFlag({skip: config?.skip});

  React.useEffect(() => {
    if (isNeedAcceptTariffs) {
      showAcceptTariffsModal();
    }
  }, [isNeedAcceptTariffs]);
};

export {
  NeedAcceptTariffsBanner,
  showAcceptTariffsModal,
  useAcceptTariffsAction,
  useAcceptTariffsFlag,
};
